/*
  Klaviyo "Added to Cart" tracking — 100% of add-to-carts.

  The theme adds items via fetch(theme.routes.cart_add_url) from minified
  bundles (theme.js, product-info.js), so instead of patching each call site
  we wrap window.fetch and watch for successful POSTs to /cart/add. Every
  add-to-cart path (product form, drawer upsells, color swap) goes through
  the same endpoint, so a single hook covers them all.

  Klaviyo only records onsite events for identified browsers. To capture
  anonymous visitors too, we identify them with a stable technical id
  ($id, a UUID persisted in localStorage) right before tracking. When the
  visitor later gives their email (form, checkout), Klaviyo merges the
  anonymous profile into the real one, keeping the event history.
*/
(function () {
  'use strict';

  if (window.__klAtcHooked) return;
  window.__klAtcHooked = true;

  // Official Klaviyo queue shim: queues klaviyo.push()/method calls into
  // window._klOnsite until klaviyo.js loads and drains it. Without this,
  // pushes made before the script loads are silently lost.
  if (!window.klaviyo) {
    window._klOnsite = window._klOnsite || [];
    try {
      window.klaviyo = new Proxy({}, {
        get: function (target, prop) {
          return prop === 'push'
            ? function () {
                window._klOnsite.push.apply(window._klOnsite, arguments);
              }
            : function () {
                var args = Array.prototype.slice.call(arguments);
                var callback = typeof args[args.length - 1] === 'function' ? args.pop() : undefined;
                return new Promise(function (resolve) {
                  window._klOnsite.push([prop].concat(args, [function (result) {
                    if (callback) callback(result);
                    resolve(result);
                  }]));
                });
              };
        },
      });
    } catch (e) {
      window.klaviyo = window.klaviyo || [];
      window.klaviyo.push = function () {
        window._klOnsite.push.apply(window._klOnsite, arguments);
      };
    }
  }

  // The Klaviyo app embed is only enabled on the live theme; on draft themes
  // (and as a safety net) load klaviyo.js ourselves. Guarded against double
  // loading by the script id.
  if (!document.getElementById('klaviyo-onsite-js')) {
    var alreadyThere = document.querySelector('script[src*="static.klaviyo.com/onsite/js"]');
    if (!alreadyThere) {
      var loader = document.createElement('script');
      loader.id = 'klaviyo-onsite-js';
      loader.async = true;
      loader.src = 'https://static.klaviyo.com/onsite/js/klaviyo.js?company_id=QWH3C5';
      document.head.appendChild(loader);
    }
  }

  var originalFetch = window.fetch;
  if (typeof originalFetch !== 'function') return;

  function anonymousId() {
    var KEY = '__kl_anon_id';
    var id = null;
    try { id = localStorage.getItem(KEY); } catch (e) {}
    if (!id) {
      if (window.crypto && crypto.randomUUID) {
        id = 'anon-' + crypto.randomUUID();
      } else {
        id = 'anon-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
      }
      try { localStorage.setItem(KEY, id); } catch (e) {}
    }
    return id;
  }

  // Resolves once the browser is identified: either it already is (came from
  // a Klaviyo email or filled a form), or we identify it with the anonymous
  // technical id so the event is recorded instead of dropped.
  function ensureIdentified() {
    return new Promise(function (resolve) {
      var done = false;
      var finish = function () { if (!done) { done = true; resolve(); } };
      // klaviyo.js unreachable (ad blocker): don't hang the tracking path.
      setTimeout(finish, 4000);
      try {
        window.klaviyo.isIdentified()
          .then(function (identified) {
            if (!identified) {
              window.klaviyo.push(['identify', { $id: anonymousId() }]);
            }
            finish();
          })
          .catch(finish);
      } catch (e) {
        finish();
      }
    });
  }

  function isCartAddUrl(url) {
    if (!url) return false;
    try {
      var path = new URL(url, window.location.origin).pathname;
      // Matches /cart/add and /cart/add.js, with or without a locale prefix
      return /\/cart\/add(\.js)?$/.test(path);
    } catch (e) {
      return false;
    }
  }

  function toMoney(cents) {
    return Math.round(cents) / 100;
  }

  function absoluteUrl(path) {
    if (!path) return undefined;
    return path.indexOf('http') === 0 ? path : window.location.origin + path;
  }

  function trackAddedToCart(addedItems) {
    // Pull the full cart so the event carries cart value and contents,
    // matching Klaviyo's standard Shopify "Added to Cart" payload.
    originalFetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var added = addedItems[0];
        if (!added) return;

        var properties = {
          $value: toMoney(cart.total_price),
          AddedItemProductName: added.product_title,
          AddedItemProductID: added.product_id,
          AddedItemVariantID: added.variant_id,
          AddedItemVariantName: added.variant_title,
          AddedItemSKU: added.sku,
          AddedItemPrice: toMoney(added.price),
          AddedItemQuantity: added.quantity,
          AddedItemURL: absoluteUrl(added.url),
          AddedItemImageURL: added.image,
          ItemNames: cart.items.map(function (item) { return item.product_title; }),
          CheckoutURL: window.location.origin + '/checkout',
          Items: cart.items.map(function (item) {
            return {
              ProductID: item.product_id,
              VariantID: item.variant_id,
              SKU: item.sku,
              ProductName: item.product_title,
              VariantName: item.variant_title,
              Quantity: item.quantity,
              ItemPrice: toMoney(item.price),
              RowTotal: toMoney(item.final_line_price),
              ProductURL: absoluteUrl(item.url),
              ImageURL: item.image,
            };
          }),
        };

        ensureIdentified().then(function () {
          window.klaviyo.push(['track', 'Added to Cart', properties]);
        });
      })
      .catch(function () {
        /* tracking must never break the storefront */
      });
  }

  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : input && input.url;
    var method = (init && init.method) || (input && input.method) || 'GET';
    var result = originalFetch.apply(this, arguments);

    if (method.toUpperCase() === 'POST' && isCartAddUrl(url)) {
      result
        .then(function (response) {
          if (!response.ok) return;
          response
            .clone()
            .json()
            .then(function (data) {
              // /cart/add.js returns the line item directly for a single add,
              // or {items: [...]} when several items are posted at once. A
              // failed add responds with {status: 422, ...} and no item data.
              if (data.status) return;
              var items = data.items || [data];
              if (items.length && items[0].product_id) trackAddedToCart(items);
            })
            .catch(function () {});
        })
        .catch(function () {});
    }

    return result;
  };
})();
